import { useNookSites } from "@nook/api-client"
import {
  Button,
  Calendar,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@nook/ui"
import { formatShortDate } from "@nook/client-ui"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { CalendarDays, Check, ChevronDown, Globe, X } from "lucide-react"
import { useState } from "react"

import { useKeys } from "@/components/keys.js"
import { DATE_PRESETS, dayStart, localDay, matchPreset, presetRange, type BrowseSearch } from "@/lib/browse.js"

/**
 * The library's Source and Date filters (block 19), opened by `F` and `D`.
 * Source lists every host the user has saved from, searchable. Each change
 * replaces the URL, so Back leaves the library instead of stepping back
 * through filter clicks.
 */
export function BrowseFilters({ sources = true }: { sources?: boolean }) {
  const search: BrowseSearch = useSearch({ strict: false })
  const navigate = useNavigate()
  const sites = useNookSites().data?.items ?? []
  const picked = search.site ?? []
  const [open, setOpen] = useState<"site" | "date" | null>(null)
  // Presets first; the calendar is one step further in.
  const [custom, setCustom] = useState(false)
  const active = matchPreset(search.from, search.to)

  const update = (next: BrowseSearch) =>
    void navigate({ to: ".", search: (prev: BrowseSearch) => ({ ...prev, ...next }), replace: true })

  const clear = () => update({ site: undefined, from: undefined, to: undefined })

  useKeys({ f: () => sources && setOpen("site"), d: () => setOpen("date"), "Shift+x": clear })

  const toggle = (host: string) => {
    const next = picked.includes(host) ? picked.filter((h) => h !== host) : [...picked, host]
    update({ site: next.length ? next.sort() : undefined })
  }

  const range = active
    ? DATE_PRESETS.find(([preset]) => preset === active)![1]
    : search.from
    ? `${formatShortDate(dayStart(search.from).toISOString())} – ${search.to ? formatShortDate(dayStart(search.to).toISOString()) : "now"}`
    : null

  const popover = (name: "site" | "date") => ({
    open: open === name,
    onOpenChange: (next: boolean) => {
      setOpen(next ? name : null)
      setCustom(false)
    },
  })

  return (
    <div className="flex items-center gap-2">
      {sources ? (
        <Popover {...popover("site")}>
        <PopoverTrigger asChild>
          <Button variant={picked.length ? "secondary" : "outline"} size="sm" className="h-7.5 gap-1.5 pr-1.5 pl-2.5 text-[0.8125rem] font-normal">
            {picked.length === 1 ? picked[0] : picked.length ? `${picked.length} sources` : "Source"}
            <ChevronDown />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-0">
          <Command>
            <CommandInput placeholder="Find a source…" />
            <CommandList>
              <CommandEmpty>No sources.</CommandEmpty>
              <CommandItem value="All sources" onSelect={() => update({ site: undefined })}>
                <Globe />
                All sources
                <Check className={`ml-auto ${picked.length ? "invisible" : ""}`} />
              </CommandItem>
              <CommandSeparator />
              {sites.map(({ host, icon }) => (
                <CommandItem key={host} value={host} onSelect={() => toggle(host)}>
                  {icon ? <img src={icon} alt="" className="size-4 rounded-sm" /> : <Globe />}
                  <span className="truncate">{host}</span>
                  <Check className={`ml-auto ${picked.includes(host) ? "" : "invisible"}`} />
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
        </Popover>
      ) : null}

      <Popover {...popover("date")}>
        <PopoverTrigger asChild>
          <Button variant={range ? "secondary" : "outline"} size="sm" className="h-7.5 gap-1.5 pr-1.5 pl-2.5 text-[0.8125rem] font-normal">
            <CalendarDays />
            {range ?? "Date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          {custom ? (
            <Calendar
              mode="range"
              selected={search.from ? { from: dayStart(search.from), to: search.to ? dayStart(search.to) : undefined } : undefined}
              onSelect={(next) => update({ from: next?.from && localDay(next.from), to: next?.to && localDay(next.to) })}
              disabled={{ after: new Date() }}
            />
          ) : (
            <Command>
              <CommandList>
                {DATE_PRESETS.map(([preset, label]) => (
                  <CommandItem
                    key={preset}
                    onSelect={() => {
                      update(presetRange(preset))
                      setOpen(null)
                    }}
                  >
                    {label}
                    <Check className={`ml-auto ${preset === active ? "" : "invisible"}`} />
                  </CommandItem>
                ))}
                <CommandSeparator />
                <CommandItem onSelect={() => setCustom(true)}>
                  Custom range…
                  <Check className={`ml-auto ${search.from && !active ? "" : "invisible"}`} />
                </CommandItem>
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>

      {picked.length || search.from ? (
        <Button variant="ghost" size="sm" className="h-7.5 text-[0.8125rem]" onClick={clear}>
          <X />
          Clear
        </Button>
      ) : null}
    </div>
  )
}
